import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { logout } from '../../api/auth';
import { useRecognitionDayOpen } from './useRecognitionDayOpen';
import { useSession } from './useSession';

/** Idle timeout when no recognition day is open (plan: admin idle auto-logout). */
export const IDLE_LOGOUT_MS = 10 * 60 * 1000;

const ACTIVITY_EVENTS = [
  'pointerdown',
  'keydown',
  'touchstart',
  'scroll',
] as const;

/**
 * Logs the admin out after {@link IDLE_LOGOUT_MS} of user inactivity when the
 * current scope has no open recognition day. Background polls/SSE do not reset
 * the timer. While a recognition day is open, the idle timer is disabled.
 * Hidden closed-day tabs keep this deadline; their network work is stopped
 * separately by useDocumentVisible gates and useLiveEvents.
 */
export function useIdleLogout(): void {
  const { me, loading } = useSession();
  const dayOpen = useRecognitionDayOpen();
  const { pathname, search } = useLocation();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    if (loading || !me || dayOpen === null) {
      clearTimer();
      return;
    }

    if (dayOpen) {
      clearTimer();
      return;
    }

    const arm = () => {
      clearTimer();
      timerRef.current = setTimeout(() => {
        void logout();
      }, IDLE_LOGOUT_MS);
    };

    const onActivity = () => {
      arm();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        arm();
      }
    };

    arm();
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { passive: true });
    }
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearTimer();
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity);
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [me, loading, dayOpen, pathname, search]);
}
